import _ from 'lodash';
import uuidv4 from 'uuid/v4';
import { has, addFileSufix, getFileExtension } from '../../../../utils.js';
import updater from '../../updater.js';
import iconMetadata from '../../../icon-metadata.js';

/**
 * Asset reference related getters that operate on a "flat" json structure (requires the basic getters).
 */
function getters() {
  return {
    /**
     * Return all the assets of a specific node
     * @param {string}          id  The node id
     * @returns {Object|null}       The assets of the node
     */
    getAssets(state, getters) {
      return id => {
        const node = getters.getNode(id);
        if (!node || !has(node, 'assets')) {
          return null;
        }
        return node.assets;
      };
    },

    /**
     * Return the cover asset reference for a specific node
     * @param   {String}        id   The node id
     * @return  {Object|null}        The cover asset reference { id } or { type, file }
     */
    getCover(state, getters) {
      return id => {
        const node = getters.getNode(id);
        if (!node || !has(node, 'assets') || !has(node.assets, 'cover')) {
          return null;
        }
        return node.assets.cover;
      };
    },

    /**
     * Return the cover asset id (if exists) for a specific node
     * @param   {String}      id    The node id
     * @return  {String|null}
     */
    getCoverId(state, getters) {
      return id => {
        const cover = getters.getCover(id);
        if (!cover) return null;
        return cover.id || null;
      };
    },

    /**
     * Return the cover icon (if exists) for a specific node
     * @param   {String}        id    The node id
     * @return  {String|null}
     */
    getCoverIcon(state, getters) {
      return id => {
        const cover = getters.getCover(id);
        if (!cover || cover.type !== 'icon') return null;
        return cover.file;
      };
    },

    /**
     * Return the other asset references for a specific node
     * @param   {String} id   The node id
     * @return  {Array}       The other asset reference objects { id } or { type, file }
     */
    getOther(state, getters) {
      return id => {
        const node = getters.getNode(id);
        if (!node || !has(node, 'assets') || !has(node.assets, 'other')) {
          return null;
        }
        return node.assets.other;
      };
    },

    /**
     * Return the icon of the specific reference (if icon asset)
     * @param   {Object}      assetReference  The asset reference
     * @return  {String|null}
     */
    getIcon(state) {
      return assetReference => {
        const { type, file } = assetReference;
        if (!type || type !== 'icon') return null;
        return file;
      };
    },

    /**
     * Return the default icon
     * @return  {String}
     */
    getDefaultIcon(state) {
      return 'Arcweave/man';
    },

    /**
     * Return the file property of the specific reference
     * @param   {Object}      assetReference  The asset reference
     * @return  {String|null}
     */
    getAssetFile(state, getters, rootState, rootGetters) {
      return assetReference => {
        const { type, file, id } = assetReference;
        if (type === 'icon') return file;
        if (id) {
          return rootGetters['project/data/assets/getFile'](id);
        }
        return null;
      };
    },

    /**
     * Return the file extension of the specific reference
     * @param {Object}        assetReference The asset reference
     * @return {String|null}
     */
    getAssetExtension(state, getters, rootState, rootGetters) {
      return assetReference => {
        const { type, id } = assetReference;
        if (type === 'icon') return 'svg';
        if (id) {
          return rootGetters['project/data/assets/getExtension'](id);
        }
        return null;
      };
    },

    /**
     * Return the name of the asset given its reference
     * @param   {Object}        assetReference  The asset reference
     * @return  {String|null}
     */
    getAssetName(state, getters, rootState, rootGetters) {
      return assetReference => {
        if (!assetReference) return null;
        const { type, file, id } = assetReference;
        if (type === 'icon') return iconMetadata.getIconName(file);
        if (id) {
          return rootGetters['project/data/assets/getName'](id);
        }
        return null;
      };
    },

    /**
     * Return the size of the asset given its reference
     * @param {Object} assetReference  The asset reference
     * @return {String|null}
     */
    getAssetSize(state, getters, rootState, rootGetters) {
      return assetReference => {
        if (!assetReference) return null;
        const { id } = assetReference;
        if (id) {
          return rootGetters['project/data/assets/getSize'](id);
        }
        return null;
      };
    },

    /**
     * Return the nodes referencing some assets.
     * @param {Array} assetIds  The asset ids
     * @return {Array}
     */
    getAssetReferences(state) {
      return assetIds => {
        // console.log('Searching for asset ids:', assetIds);
        const assetHash = {};
        Object.values(state.byId).forEach(node => {
          const { assets } = node;
          if (!assets) return;
          const coverId = assets?.cover?.id;
          if (coverId && assetIds.includes(coverId)) {
            if (!has(assetHash, coverId)) {
              assetHash[coverId] = [];
            }
            assetHash[coverId].push(node);
          }
          if (Array.isArray(assets?.audio)) {
            const processedAssets = new Set();
            assets.audio.forEach(assetRef => {
              if (assetIds.includes(assetRef.asset)) {
                if (!processedAssets.has(assetRef.asset)) {
                  if (!has(assetHash, assetRef.asset)) {
                    assetHash[assetRef.asset] = [];
                  }
                  assetHash[assetRef.asset].push(node);
                  processedAssets.add(assetRef.asset);
                }
              }
            });
          }
        });
        return assetHash;
      };
    },

    /**
     * Returns if there is at least one audio reference
     * @returns {Boolean}
     */
    getAudioReferencesExist(state) {
      let found = false;
      Object.values(state.byId).every(node => {
        const audioAssets = node?.assets?.audio;
        if (Array.isArray(audioAssets) && audioAssets.length > 0) {
          found = true;
          return false;
        }
        return true;
      });
      return found;
    },
  };
}

function actions(options) {
  return {
    /**
     * Create a cover asset reference
     * @param {String}  id         The node id
     * @param {Object}  cover      The cover object { id } or { type, file }
     * @param {Boolean} apply      Whether to apply the patch after finishing
     */
    setCover(context, { id, cover, apply }) {
      const { type } = options;
      const assets = context.getters.getAssets(id);
      const patch = [];

      if (!assets) {
        patch.push({
          op: 'add',
          path: `/data/${type}/byId/${id}/assets`,
          value: { cover },
        });
      } else {
        patch.push({
          op: 'add',
          path: `/data/${type}/byId/${id}/assets/cover`,
          value: cover,
        });
      }

      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Remove a cover asset reference
     * @param {String}  id        The node id
     * @param {Boolean} apply     Whether to apply the patch after finishing
     */
    removeCover(context, { id, apply }) {
      const { type } = options;
      const assets = context.getters.getAssets(id);
      if (Object.keys(assets).length === 1 && has(assets, 'cover')) {
        updater.add([
          {
            op: 'remove',
            path: `/data/${type}/byId/${id}/assets`,
          },
        ]);
      } else {
        updater.add([
          {
            op: 'remove',
            path: `/data/${type}/byId/${id}/assets/cover`,
          },
        ]);
      }
      updater.complete({ apply });
    },

    /**
     * Remove all asset references from this structure
     * @param {Array}  ids        The node ids
     * @param {Boolean} apply      Whether to apply the patch after finishing
     */
    removeAssetReferences(context, { ids, apply }) {
      Object.values(context.state.byId).forEach(node => {
        if (has(node, 'assets')) {
          if (
            has(node.assets, 'cover') &&
            has(node.assets.cover, 'id') &&
            ids.includes(node.assets.cover.id)
          ) {
            console.log('Removing cover for node', node);
            context.dispatch('removeCover', { id: node.id, apply: false });
          }
          if (Array.isArray(node?.assets?.audio)) {
            for (let i = node.assets.audio.length - 1; i >= 0; i -= 1) {
              const assetRef = node.assets.audio[i];
              if (ids.includes(assetRef.asset)) {
                context.dispatch('deleteAudio', {
                  id: node.id,
                  index: i,
                  apply: false,
                });
              }
            }
          }
        }
      });
      updater.complete({ apply });
    },

    /**
     * Add an audio asset reference to a node audio asset list
     * @param {string}  id        The node id
     * @param {string}  audioId   The audio asset ID
     * @param {Number}  index     The index in the list
     * @param {Boolean} apply     Whether to apply the patch
     */
    addAudio(context, { id, audioId, index, apply }) {
      const { type } = options;
      const assets = context.getters.getAssets(id);
      const referenceAudio = {
        id: uuidv4(),
        asset: audioId,
        mode: 'once',
        delay: 0,
      };
      const patch = [];
      if (!assets) {
        patch.push({
          op: 'add',
          path: `/data/${type}/byId/${id}/assets`,
          value: { audio: [referenceAudio] },
        });
      } else if (!assets.audio) {
        patch.push({
          op: 'add',
          path: `/data/${type}/byId/${id}/assets/audio`,
          value: [referenceAudio],
        });
      } else if (assets.audio && Array.isArray(assets.audio)) {
        const state = _.cloneDeep(context.getters.getAssets(id).audio);
        patch.push({
          op: 'add',
          path: `/data/${type}/byId/${id}/assets/audio/${index}`,
          value: referenceAudio,
          state,
        });
      }

      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Remove an audio asset reference from an node's audio asset list
     * @param {String}  id      The node ID
     * @param {Number}  index   The index in the list
     * @param {Boolean} apply   Whether to apply the patch
     */
    deleteAudio(context, { id, index, apply }) {
      const { type } = options;
      const state = _.cloneDeep(context.getters.getAssets(id));
      if (state.audio.length === 1) {
        if (Object.keys(state).length === 1 && has(state, 'audio')) {
          updater.add([
            {
              op: 'remove',
              path: `/data/${type}/byId/${id}/assets`,
            },
          ]);
        } else {
          updater.add([
            {
              op: 'remove',
              path: `/data/${type}/byId/${id}/assets/audio`,
            },
          ]);
        }
      } else {
        updater.add([
          {
            op: 'remove',
            path: `/data/${type}/byId/${id}/assets/audio/${index}`,
            state: state.audio,
          },
        ]);
      }

      updater.complete({ apply });
    },

    /**
     * Move an audio asset reference
     * @param {String}  sId     The source node ID
     * @param {Number}  sIndex  The source index
     * @param {String}  tId     The target node ID
     * @param {Number}  tIndex  The target index
     * @param {Boolean} apply   Whether to apply the patch
     */
    moveAudio(context, { sId, sIndex, tId, tIndex, apply }) {
      const { type } = options;
      const fromAssets = _.cloneDeep(context.getters.getAssets(sId));
      const toAssets = _.cloneDeep(context.getters.getAssets(tId));

      const asset = fromAssets.audio[sIndex];
      const patch = [];
      let state = [];
      if (!toAssets) {
        patch.push({
          op: 'add',
          path: `/data/${type}/byId/${tId}/assets`,
          value: { audio: [] },
        });
        tIndex = 0;
      } else if (!toAssets.audio) {
        patch.push({
          op: 'add',
          path: `/data/${type}/byId/${tId}/assets/audio`,
          value: [],
        });
        tIndex = 0;
      } else {
        const index = toAssets.audio.findIndex(el => _.isEqual(el, asset));
        if (index !== -1 && index < tIndex) {
          tIndex -= 1;
        }
        state = toAssets.audio;
      }

      patch.push({
        op: 'move',
        from: `/data/${type}/byId/${sId}/assets/audio/${sIndex}`,
        path: `/data/${type}/byId/${tId}/assets/audio/${tIndex}`,
        fromState: fromAssets.audio,
        state,
      });

      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Update an asset reference
     * @param {String}  id        The node ID
     * @param {Number}  index     The asset reference index
     * @param {Object}  props     The properties to be updated
     * @param {Boolean} apply     Whether to apply the patch
     */
    updateAudio(context, { id, index, props, apply }) {
      const { type } = options;
      const assetRef = _.cloneDeep(context.getters.getAssets(id).audio[index]);
      updater.add([
        {
          op: 'replace',
          path: `/data/${type}/byId/${id}/assets/audio/${index}`,
          value: {
            ...assetRef,
            ...props,
          },
        },
      ]);
      updater.complete({ apply });
    },
  };
}

export default { getters, actions };
